import { useUIStore } from '@/stores/uiStore';
import { DrugBadges } from '@/entities/drug/components/DrugBadges';
import type { DrugCard } from '@/entities/drug/api/drug.api';
import { useDrugDetail } from '@/entities/drug/hooks/useDrugQueries';
import {
  formatFeeRate,
  isLinkableDrugCode,
} from '@/entities/drug/utils/drug.utils';
import { useDrugDrawer } from '@/shared/components/drug-drawer/useDrugDrawer';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/shared/components/ui/hover-card';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/lib/utils';
import { formatWon } from '@/shared/utils/format';
import { useSearch } from '@tanstack/react-router';
import { useSyncExternalStore, type ReactNode } from 'react';

/** 마우스로 호버할 수 있는 데스크톱(≥1024)에서만 미리보기를 띄운다 (M-15 A · README §8) */
const HOVER_QUERY =
  '(hover: hover) and (pointer: fine) and (min-width: 1024px)';
const subscribeHover = (onChange: () => void) => {
  const mql = window.matchMedia(HOVER_QUERY);
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
};
const useCanHover = () =>
  useSyncExternalStore(
    subscribeHover,
    () => window.matchMedia(HOVER_QUERY).matches,
    () => false,
  );

/**
 * 약명 링크 — 클릭하면 의약품 상세 드로어(M-12)를 연다.
 * 실적 · 검색 · 동일성분 등 약명이 나오는 모든 곳에서 이 컴포넌트를 쓴다.
 * 의약품 정보가 없는 품목(drugCode가 "-" 또는 ru…)은 링크 없이 텍스트만 보여준다.
 *
 * M-15 A 호버 미리보기: 300ms 머무르면 열리고 150ms 뒤 닫힌다.
 * 터치 기기 · 모바일 폭 · 드로어가 이미 열려 있을 때 · 키보드 포커스만 옮겼을 때는 띄우지 않는다.
 */
export const DrugNameLink = ({
  drugCode,
  children,
  className,
  preview,
  context,
}: {
  drugCode?: string | null;
  children: ReactNode;
  className?: string;
  /** 검색 카드처럼 카드 값을 이미 가진 화면은 넘겨서 바로 그린다 (100회 예상액만 상세에서 받는다) */
  preview?: DrugCard;
  /** 실적 화면처럼 "그 달 그 병의원" 처방량을 드로어에 함께 보여 줄 때 (M-15 C) */
  context?: { label: string; quantity: number | null };
}) => {
  const { open } = useDrugDrawer();
  const setDrugContext = useUIStore((s) => s.setDrugContext);
  const { drug: openedDrug } = useSearch({ strict: false }) as {
    drug?: string;
  };
  const canHover = useCanHover();

  if (!isLinkableDrugCode(drugCode))
    return <span className={className}>{children}</span>;

  const link = (
    <button
      type="button"
      onClick={(e) => {
        // 카드 전체 클릭(M-11)과 겹쳐 history가 두 번 쌓이지 않게 막는다
        e.stopPropagation();
        setDrugContext(context ? { drugCode, ...context } : null);
        void open(drugCode);
      }}
      // 키보드 포커스만으로는 미리보기를 열지 않는다 (Radix는 포커스에도 열기 때문에 기본 동작을 막는다)
      onFocus={(e) => e.preventDefault()}
      className={cn('text-left hover:underline', className)}
    >
      {children}
    </button>
  );

  if (!canHover || openedDrug) return link;

  return (
    <HoverCard openDelay={300} closeDelay={150}>
      <HoverCardTrigger asChild>{link}</HoverCardTrigger>
      <HoverCardContent className="w-[280px] px-[18px] pt-4 pb-3.5">
        <LazyDrugPreview drugCode={drugCode} card={preview} />
      </HoverCardContent>
    </HoverCard>
  );
};

/**
 * 호버 카드가 열릴 때 상세를 불러온다 (드로어와 같은 캐시). 100회 처방 예상액은 상세에만 있다.
 * 카드 값을 넘긴 화면(M-11)은 상세를 기다리지 않고 카드로 먼저 그린다.
 */
const LazyDrugPreview = ({
  drugCode,
  card,
}: {
  drugCode: string;
  card?: DrugCard;
}) => {
  const { data, isPending, isError } = useDrugDetail(drugCode);
  const drug = card ?? data?.drug;

  if (!drug && isPending)
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-3 w-1/3" />
        <Skeleton className="h-9 w-full" />
      </div>
    );
  if (!drug || (isError && !card))
    return (
      <p className="text-[11px] text-ink-7">의약품 정보를 불러오지 못했어요.</p>
    );
  return (
    <DrugPreviewBody
      drug={drug}
      estimated={data?.pharmaEstimatedAmount}
      estimatedLoading={isPending}
    />
  );
};

const DrugPreviewBody = ({
  drug,
  estimated,
  estimatedLoading,
}: {
  drug: DrugCard;
  estimated: number | null | undefined;
  estimatedLoading: boolean;
}) => {
  const stats = [
    { label: '약가', value: formatWon(drug.price), tone: 'text-ink' },
    {
      label: '수수료율',
      value: formatFeeRate(drug.pharmaFeeRate),
      tone: drug.pharmaFeeRate == null ? 'text-[#bbb]' : 'text-primary',
    },
    {
      label: '100회',
      value: estimatedLoading ? '…' : formatWon(estimated),
      tone: estimated == null ? 'text-[#bbb]' : 'text-success',
    },
  ];

  return (
    <div className="flex flex-col">
      <DrugBadges
        isReferenceDrug={drug.referenceDrug}
        isBioequivalent={drug.bioequivalent}
        className="mb-2 gap-[5px]"
      />
      <p className="text-xs leading-[18px] font-bold text-ink">
        {drug.drugName}
      </p>
      <p className="mt-1 text-[10px] leading-[15px] text-ink-7">
        {[drug.pharmaCompanyName, drug.reimbursementType]
          .filter(Boolean)
          .join(' · ')}
      </p>
      <div className="mt-3 flex">
        {stats.map((s, i) => (
          <div key={s.label} className="flex flex-1">
            {i > 0 && <span className="h-6 w-px bg-line-soft" />}
            <div className={cn('flex flex-col gap-[3px]', i > 0 && 'pl-2')}>
              <span className="text-[9px] leading-[14px] text-ink-7">
                {s.label}
              </span>
              <span className={cn('text-xs leading-[18px] font-bold', s.tone)}>
                {s.value}
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 h-px bg-[#f0f0f0]" />
      <p className="mt-2.5 text-[10px] leading-[15px] text-ink-6">
        클릭하면 상세 드로어가 열립니다
      </p>
    </div>
  );
};
